import { useNotes } from "../lib/notesContext";
import { overlaps, monthBounds } from "../lib/notes";
import { useMemo, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from "recharts";
import { KpiCards } from "./KpiCards";
import { MetricToggle } from "./MetricToggle";
import { ChartTooltip } from "./ChartTooltip";
import { MonthSpotlight } from "./MonthSpotlight";
import { fmtCompact } from "../lib/format";

const SERIES = {
  visits: { name: "Visits", color: "#D4333B" },
  qty: { name: "Qty picked", color: "#10B981" },
  vph: { name: "Visits / hr", color: "#F59E0B" },
  pph: { name: "Picks / hr", color: "#06B6D4" },
};

const withDeltas = (rows, keys) => rows.map((r, i) => {
  const o = { ...r };
  keys.forEach((k) => {
    const p = rows[i - 1]?.[k];
    o[`${k}_d`] = i && p && r[k] != null ? +(((r[k] - p) / p) * 100).toFixed(1) : null;
  });
  return o;
});

export const Overview = ({ data, hoursByMonth, onOpenUser }) => {
  const { notes } = useNotes();
  const noted = data.overall.filter((o) => notes.some((n) => overlaps(n, monthBounds(o.month).start, monthBounds(o.month).end) && n.period !== "range"));
  const [metric, setMetric] = useState(data.hasQty ? "both" : "visits");
  const [showDelta, setShowDelta] = useState(false);
  const [month, setMonth] = useState(data.months[data.months.length - 1]);
  const hasHours = !!hoursByMonth;
  const keys = metric === "both" ? ["visits", "qty"] : [metric];
  const rows = useMemo(() => withDeltas(data.overall.map((o) => {
    const h = (hoursByMonth?.[o.month] || 0) / 3600;
    return { ...o, vph: h ? +(o.visits / h).toFixed(1) : null };
  }), ["visits", "qty", "vph", "pph"]), [data.overall, hoursByMonth]);
  const opts = [...(data.hasQty ? [["both", "Both"], ["visits", "Visits"], ["qty", "Qty picked"]] : [["visits", "Visits"]]), ...(hasHours ? [["vph", "Visits / hr"]] : []), ...(data.hasHop ? [["pph", "Picks / hr"]] : [])];
  const onClick = (e) => {
    const idx = Number(e?.activeTooltipIndex ?? e?.activeIndex);
    const pt = !isNaN(idx) ? data.overall[idx] : data.overall.find((o) => o.label === e?.activeLabel);
    if (pt) setMonth(pt.month);
  };
  const dot = useMemo(() => (props) => {
    const { cx, cy, payload, stroke } = props;
    if (cx == null || cy == null) return null;
    const on = payload.month === month;
    return <circle key={`${payload.month}-${stroke}`} cx={cx} cy={cy} r={on ? 6 : 2.5} fill={on ? stroke : "#0B0F19"} stroke={stroke} strokeWidth={2} />;
  }, [month]);
  const axis = { tick: { fontSize: 11 }, tickLine: false, axisLine: false, width: 48 };
  const xAxis = <XAxis dataKey="label" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#1F2937" }} />;

  return (
    <div className="space-y-6">
      <KpiCards data={data} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="panel p-5 sm:p-6 lg:col-span-8" data-testid="monthly-line-chart">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-100">Monthly timeline</h3>
              <p className="text-xs text-slate-500">Click any month to inspect it{noted.length ? " · dashed amber = month has notes" : ""}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <MetricToggle value={metric} onChange={setMetric} options={opts} />
              <button data-testid="toggle-delta-lines" onClick={() => setShowDelta(!showDelta)} className={`text-xs px-3 py-2 rounded-md border transition-colors ${showDelta ? "border-amber-500/60 bg-amber-500/10 text-amber-300" : "border-slate-700 text-slate-400 hover:bg-slate-800"}`}>Δ% lines</button>
            </div>
          </div>
          <div className="h-[340px] cursor-pointer">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} onClick={onClick} syncId="overview" margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#1F2937" vertical={false} />
                {xAxis}
                {keys.map((k, i) => <YAxis key={k} yAxisId={k} orientation={i ? "right" : "left"} stroke={SERIES[k].color} tickFormatter={fmtCompact} {...axis} />)}
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#334155" }} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                {noted.map((o) => <ReferenceLine key={o.month} yAxisId={keys[0]} x={o.label} stroke="#F59E0B" strokeOpacity={0.5} strokeDasharray="2 4" label={{ value: "✎", fill: "#F59E0B", fontSize: 12, position: "insideTop" }} />)}
                {keys.map((k) => <Line key={k} yAxisId={k} type="monotone" dataKey={k} name={SERIES[k].name} stroke={SERIES[k].color} strokeWidth={2.5} dot={dot} activeDot={{ r: 6 }} connectNulls />)}
              </LineChart>
            </ResponsiveContainer>
          </div>
          {showDelta && (
            <div className="mt-6" data-testid="delta-chart">
              <p className="label-mono mb-2">Change vs previous month (%)</p>
              <div className="h-[180px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={rows} syncId="overview" margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="#1F2937" vertical={false} />
                    {xAxis}
                    <YAxis stroke="#64748B" unit="%" {...axis} />
                    <ReferenceLine y={0} stroke="#64748B" />
                    <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#334155" }} />
                    {keys.map((k) => <Line key={k} type="monotone" dataKey={`${k}_d`} name={`${SERIES[k].name} Δ%`} stroke={SERIES[k].color} strokeWidth={2} strokeDasharray="5 4" dot={{ r: 2.5 }} connectNulls />)}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
        <div className="lg:col-span-4">
          <MonthSpotlight data={data} month={month} setMonth={setMonth} onOpenUser={onOpenUser} />
        </div>
      </div>
    </div>
  );
};
